import { inject } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivateFn,
  Router,
  RouterStateSnapshot,
} from '@angular/router';
import { AuthenticationService } from '@anihis/shared/src/services/authentication.service';

/**
 * RoleGuard - Route-level authorization for OpenIddict JWT tokens
 *
 * Expects route data to contain `roles: string[]` with allowed role names.
 * Reads user role from OpenIddict JWT token via AuthenticationService.
 * If user's role is not in the allowed list, redirects to home.
 *
 * Usage:
 * {
 *   path: 'admin',
 *   component: AdminComponent,
 *   canActivate: [AuthGuard, RoleGuard],
 *   data: { roles: ['Administrator', 'ClinicOwner'] }
 * }
 */
export const RoleGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
) => {
  const authService = inject(AuthenticationService);
  const router = inject(Router);

  const requiredRoles = route.data['roles'] as string[] | undefined;

  // If no roles specified, allow access
  if (!requiredRoles || requiredRoles.length === 0) {
    return true;
  }

  const userRole = authService.getRoleFromToken();

  // Check if user has required role
  if (!userRole || !requiredRoles.includes(userRole)) {
    console.warn(
      `Access denied: User role '${userRole}' not in required roles [${requiredRoles.join(', ')}]`,
    );

    // Redirect all unauthorized users to the access-denied page.
    // This avoids redirecting back to the guarded shell route ("/") which
    // would cause a navigation loop when the shell itself is protected.
    router.navigate(['/access-denied']);
    return false;
  }

  return true;
};
